import { Alert, Box, LinearProgress } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useI18n } from "../../../hooks/useI18n";
import { useKairosApi } from "../../../hooks/useKairosApi";
import { isTravelSnapshot, TravelSnapshot } from "./travelTypes";

interface TravelIframeEditorProps {
  travel: TravelSnapshot;
  ctaLabel: string;
  onSubmit: (travel: TravelSnapshot) => void | Promise<void>;
}

export function TravelIframeEditor({ travel, ctaLabel, onSubmit }: TravelIframeEditorProps) {
  const { id } = useParams() as { id: string };
  const { locale, t } = useI18n();
  const kairosApi = useKairosApi();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const travelRef = useRef(travel);
  const isSubmittingRef = useRef(false);
  const [evcToken, setEvcToken] = useState<string>();
  const [iframeHeight, setIframeHeight] = useState(700);
  const [isEvcLoading, setIsEvcLoading] = useState(true);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [error, setError] = useState(false);
  const mentorUrl = import.meta.env.VITE_MENTOR_URL || "";

  useEffect(() => {
    travelRef.current = travel;
  }, [travel]);

  const mentorLocale = locale === "fr" ? "fr" : locale === "gr" ? "gr" : "en";
  const iframeUrl = useMemo(() => {
    if (!mentorUrl) return "";

    const url = new URL(mentorUrl);
    url.pathname = url.hostname.includes("sadv-iframe") ? "/" : "/iframe";
    url.search = "";
    url.hash = "";
    return url;
  }, [mentorUrl]);

  const iframeSrc = useMemo(() => {
    if (!iframeUrl || !evcToken) return "";

    const url = new URL(iframeUrl);
    url.searchParams.set("mode", "travel");
    url.searchParams.set("lang", mentorLocale);
    url.searchParams.set("evc", evcToken);
    url.searchParams.set("travel-cta-label", ctaLabel);
    return url.toString();
  }, [ctaLabel, evcToken, iframeUrl, mentorLocale]);

  useEffect(() => {
    if (iframeSrc) setIsIframeLoading(true);
  }, [iframeSrc]);

  useEffect(() => {
    let cancelled = false;

    const fetchEvcToken = async () => {
      try {
        setIsEvcLoading(true);
        setError(false);
        const { jwt } = await kairosApi!.healthRecordApi.getEvcToken(id);
        if (!cancelled) setEvcToken(jwt);
      } catch (fetchError) {
        console.error("Error fetching EVC token for travel:", fetchError);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsEvcLoading(false);
      }
    };

    fetchEvcToken();
    return () => {
      cancelled = true;
    };
  }, [id, kairosApi]);

  useEffect(() => {
    if (!iframeUrl) return;
    const expectedOrigin = iframeUrl.origin;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin || event.source !== iframeRef.current?.contentWindow) return;
      if (typeof event.data !== "object" || event.data === null) return;

      switch (event.data.type) {
        case "TRAVEL_READY": {
          const version = event.data.payload_version;
          if (version !== 1 && version !== 2) {
            setError(true);
            setIsIframeLoading(false);
            return;
          }
          iframeRef.current?.contentWindow?.postMessage(
            {
              type: "TRAVEL_SET",
              requestId: "initial-travel",
              payload_version: version,
              payload: version === 1 ? { journey: travelRef.current.journey } : { travel: travelRef.current },
            },
            expectedOrigin,
          );
          break;
        }
        case "TRAVEL_STATE":
        case "TRAVEL_CHANGED": {
          // v1 only exchanges the journey; retain the questionnaire data it cannot edit.
          const nextTravel =
            event.data.payload_version === 1
              ? { ...travelRef.current, journey: event.data.payload?.journey }
              : event.data.payload_version === 2 && event.data.payload?.travel
                ? {
                    ...event.data.payload.travel,
                    viewedQuestionnaireSectionIds:
                      event.data.payload.travel.viewedQuestionnaireSectionIds === undefined
                        ? travelRef.current.viewedQuestionnaireSectionIds
                        : event.data.payload.travel.viewedQuestionnaireSectionIds,
                  }
                : undefined;
          if (!isTravelSnapshot(nextTravel)) break;
          if (event.data.type === "TRAVEL_STATE") {
            setIsIframeLoading(false);
          } else if (!isSubmittingRef.current) {
            isSubmittingRef.current = true;
            Promise.resolve(onSubmit(nextTravel)).finally(() => {
              isSubmittingRef.current = false;
            });
          }
          break;
        }
        case "TRAVEL_ERROR":
          console.error("Travel iframe error:", event.data.payload);
          setError(true);
          setIsIframeLoading(false);
          break;
        case "heightChange":
          if (typeof event.data.data?.height === "number") {
            setIframeHeight(Math.max(event.data.data.height + 20, 700));
          }
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeUrl, onSubmit]);

  if (error || !mentorUrl) {
    return <Alert severity="error">{t("travel.iframe_error")}</Alert>;
  }

  return (
    <Box sx={{ position: "relative", minHeight: 200 }}>
      {(isEvcLoading || isIframeLoading) && <LinearProgress variant="query" sx={{ mb: 1 }} />}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        width="100%"
        height={iframeHeight}
        title={t("travel.iframe_title")}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        style={{
          border: 0,
          opacity: isEvcLoading || isIframeLoading ? 0 : 1,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
}
